/**
 * Learn more about createBottomTabNavigator:
 * https://reactnavigation.org/docs/bottom-tab-navigator
 */

import { Feather, Ionicons, MaterialIcons } from '@expo/vector-icons';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import * as React from 'react';
import { useEffect, useState } from 'react';
import Colors from '../constants/Colors';
import { NavigatorScreenParams } from '@react-navigation/core';
import StudiesNavigator, { StudiesParamList } from './StudiesNavigator';
import {
  Platform,
  useWindowDimensions,
  StyleProp,
  ViewStyle
} from 'react-native';
import styled from 'styled-components/native';
import { createStackNavigator } from '@react-navigation/stack';
import { RewardsParamList } from './RewardsNavigator';
import ToDoNavigator from './ToDoNavigator';
import * as ScreenOrientation from 'expo-screen-orientation';
import AppHeader from '../components/AppHeader';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import MyProfileSettingsNavigator from './MyProfileSettingsNav';
import MessengerNavigator from './MessengerNav';
import ExamplesNavigator from './ExamplesNavigator';
import { useUserContext } from '../context/UserContext';
import DiscoverNavigator, { DiscoverParamList } from './DiscoverNavigator';
import MyDataNavigator from './MyDataNav';
import elementIDs from '../../elementIDs';
import ManageWearableNavigator from './ManageWearableNavigator';

const StyledIconContainer = styled.View`
  padding-top: 6px;
  height: 100%;
  width: 78px;
  align-items: center;
`;

const StyledIndicator = styled.View<{ focused: boolean }>`
  border-top-color: ${p => (p.focused ? 'white' : 'transparent')};
  border-top-width: 4px;
  width: 100%;
  top: -5px;
  position: absolute;
`;

const StyledTabItemText = styled.Text<{ focused: boolean }>`
  font-family: 'DMSans-Bold';
  font-size: 14px;
  line-height: 26.48px;
  top: 34px;
  letter-spacing: -0.11px;
  color: ${p => (p.focused ? 'white' : 'rgba(249, 246, 245, .5)')};
  position: absolute;
`;

export type BottomTabParamList = {
  DiscoverNav: NavigatorScreenParams<DiscoverParamList>;
  // HomeNav: NavigatorScreenParams<HomeParamList>;
  StudiesNav: NavigatorScreenParams<StudiesParamList>;
  RewardsNav: NavigatorScreenParams<RewardsParamList>;
  ToDoNav: undefined;
  MessengerNav: undefined;
  MyProfileSettingsNav: undefined;
  ExamplesNav: undefined;
  MyDataNav: undefined;
  ManageWearableNav: undefined;
};

let BottomTab = createBottomTabNavigator<BottomTabParamList>();
if (Platform.OS === 'web') {
  BottomTab = createStackNavigator<BottomTabParamList>() as any;
}

export default function BottomTabNavigator() {
  const window = useWindowDimensions();
  const isTablet = window.width >= 701 && window.width <= 1024;

  const [orientation, setOrientation] = useState(
    ScreenOrientation.Orientation.PORTRAIT_UP
  );

  useEffect(() => {
    ScreenOrientation.getOrientationAsync().then(info => {
      setOrientation(info);
    });

    const subscription = ScreenOrientation.addOrientationChangeListener(evt => {
      setOrientation(evt.orientationInfo.orientation);
    });

    return () => {
      ScreenOrientation.removeOrientationChangeListener(subscription);
    };
  }, []);

  function getTabItemStyle(type: number): StyleProp<ViewStyle> {
    if (type > 2 && Platform.OS === 'android') {
      return { top: -5, height: 85 + insets.bottom };
    } else {
      return {};
    }
  }

  const insets = useSafeAreaInsets();
  const { pastOccurrences } = useUserContext();
  return (
    <BottomTab.Navigator
      screenOptions={{
        header: () => <AppHeader />,
        tabBarActiveTintColor: 'white',
        tabBarInactiveTintColor: 'rgba(249, 246, 245, .5)',
        tabBarStyle: {
          backgroundColor: Colors.primaryPurple,
          height:
            orientation <= 2
              ? 60 + insets.bottom
              : Platform.OS === 'android'
                ? 70 + insets.bottom
                : isTablet
                  ? 70 + insets.bottom
                  : 50 + insets.bottom,
          width: '100%',
          maxWidth: 490,
          alignSelf: 'center',
          elevation: 0,
          borderTopWidth: 0,
          paddingHorizontal: 5
        },
        tabBarItemStyle: {
          paddingBottom: 5
        },
        tabBarHideOnKeyboard: true
      }}
      initialRouteName='ToDoNav'
    >
      <BottomTab.Screen
        name='ToDoNav'
        component={ToDoNavigator}
        options={{
          headerShown: false,
          title: 'Today',
          tabBarItemStyle: getTabItemStyle(orientation),
          tabBarIcon: ({ color, focused }) => (
            <StyledIconContainer>
              <StyledIndicator focused={focused} />
              <Ionicons
                name='checkmark-circle-outline'
                size={24}
                color={color}
              />
            </StyledIconContainer>
          ),
          tabBarLabel: ({ focused }) => (
            <StyledTabItemText
              testID={elementIDs.todayButtonID}
              focused={focused}
            >
              Today
            </StyledTabItemText>
          ),
          tabBarBadge:
            pastOccurrences.length > 0 ? pastOccurrences.length : undefined
        }}
      />
      <BottomTab.Screen
        name='DiscoverNav'
        component={DiscoverNavigator}
        options={{
          headerShown: false,
          title: 'Join',
          tabBarItemStyle: getTabItemStyle(orientation),
          tabBarIcon: ({ color, focused }) => (
            <StyledIconContainer>
              <StyledIndicator focused={focused} />
              <MaterialIcons name='groups' size={28} color={color} />
            </StyledIconContainer>
          ),
          tabBarLabel: ({ focused }) => (
            <StyledTabItemText
              testID={elementIDs.joinButtonID}
              focused={focused}
            >
              Join
            </StyledTabItemText>
          )
        }}
      />
      {/* <BottomTab.Screen
        name='HomeNav'
        component={HomeNavigator}
        options={{
          headerShown: false,
          title: 'Join',
          tabBarItemStyle: getTabItemStyle(orientation),
          tabBarIcon: ({ color, focused }) => (
            <StyledIconContainer>
              <StyledIndicator focused={focused} />
              <MaterialIcons name='groups' size={28} color={color} />
            </StyledIconContainer>
          ),
          tabBarLabel: ({ focused }) => (
            <StyledTabItemText focused={focused}>Join</StyledTabItemText>
          )
        }}
      /> */}
      {/* <BottomTab.Screen
        name='RewardsNav'
        component={RewardsNavigator}
        options={{
          headerShown: false,
          title: 'Rewards',
          tabBarItemStyle: getTabItemStyle(orientation),
          tabBarIcon: ({ color, focused }) => (
            <StyledIconContainer>
              <StyledIndicator focused={focused} />
              <Ionicons name='gift-outline' size={22} color={color} />
            </StyledIconContainer>
          ),
          tabBarLabel: ({ focused }) => (
            <StyledTabItemText testID={elementIDs.rewardsButtonID} focused={focused}>Rewards</StyledTabItemText>
          )
        }}
      /> */}
      <BottomTab.Screen
        name='MyDataNav'
        component={MyDataNavigator}
        options={{
          headerShown: false,
          title: 'My Data',
          tabBarItemStyle: getTabItemStyle(orientation),
          tabBarIcon: ({ color, focused }) => (
            <StyledIconContainer>
              <StyledIndicator focused={focused} />
              <Ionicons name='watch-outline' size={22} color={color} />
            </StyledIconContainer>
          ),
          tabBarLabel: ({ focused }) => (
            <StyledTabItemText
              testID={elementIDs.myDataButtonID}
              focused={focused}
            >
              My Data
            </StyledTabItemText>
          )
        }}
      />
      <BottomTab.Screen
        name='StudiesNav'
        component={StudiesNavigator}
        options={{
          headerShown: false,
          title: 'My Studies',
          tabBarItemStyle: getTabItemStyle(orientation),
          tabBarIcon: ({ color, focused }) => (
            <StyledIconContainer>
              <StyledIndicator focused={focused} />
              <Feather name='heart' size={20} color={color} />
            </StyledIconContainer>
          ),
          tabBarLabel: ({ focused }) => (
            <StyledTabItemText
              testID={elementIDs.myStudiesButtonID}
              focused={focused}
            >
              My Studies
            </StyledTabItemText>
          )
        }}
      />
      <BottomTab.Screen
        name='MessengerNav'
        component={MessengerNavigator}
        options={{
          tabBarItemStyle: { display: 'none' },
          headerShown: false
        }}
      />
      <BottomTab.Screen
        name='MyProfileSettingsNav'
        component={MyProfileSettingsNavigator}
        options={{
          tabBarItemStyle: { display: 'none' },
          headerShown: false
        }}
      />
      <BottomTab.Screen
        name='ExamplesNav'
        component={ExamplesNavigator}
        options={{
          tabBarItemStyle: { display: 'none' },
          headerShown: false
        }}
      />
      <BottomTab.Screen
        name='ManageWearableNav'
        component={ManageWearableNavigator}
        options={{
          tabBarItemStyle: { display: 'none' },
          headerShown: false
        }}
      />
    </BottomTab.Navigator>
  );
}
